import {
  COUNTRY_CODES_WITH_PHONE_VALUES,
  SUPPORTED_COUNTRIES,
} from '@application/constants/countries';
import type { SupportedPhoneNumbersEnum } from '@application/constants/countries';
import type { Language, Translate } from '@application/types';
import type { ProGetProQuery } from '@gql/graphql';
import { CountryCode, ContactPreference } from '@gql/graphql';
import type { ContactPreferenceItem } from '@presentation/components';
import dayjs from 'dayjs';
import { capitalize } from 'lodash/fp';

export const formatCurrency = (amount: number): string =>
  `${new Intl.NumberFormat('nl-BE', {
    minimumFractionDigits: 0,
  }).format(amount)}€`;

export const formatDistance = (amount: number): string =>
  `${new Intl.NumberFormat('nl-BE', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount / 1000)}km`;

export const formatBoolean = (translate: Translate, value?: boolean | null): string | undefined => {
  if (value == null) return;
  return translate(`glossary.${value ? 'yes' : 'no'}`);
};

export const anyToNumber = (value: any, fallback?: number): number | undefined => {
  const numb = Number(value);
  return Number.isNaN(numb) ? fallback : numb;
};

export const formatApiPrice = (options: {
  price?: number | null;
  priceFrom?: number | null;
  priceTo?: number | null;
  fixedPrice?: boolean | null;
}): string | undefined => {
  if (!options.fixedPrice) {
    if (options.priceFrom && options.priceTo) {
      return `${formatCurrency(options.priceFrom ?? 0)} - ${formatCurrency(options.priceTo ?? 0)}`;
    }
    if (options.priceFrom) return formatCurrency(options.priceFrom);
    if (options.priceTo) return formatCurrency(options.priceTo);
  }
  if (options.price) return formatCurrency(options.price);
};

export const formatShelterListAddress = (
  translate: Translate,
  options: {
    city?: string | null;
    region?: string | null;
    country?: string | null;
  },
): string | undefined => {
  let country: string | null = null;
  if (options.country) {
    country = translate(`enum.country.${options.country}`);
  }
  const translatedRegion = options.region
    ? translate(`enum.regionCode.${options.region}`, {
        defaultValue: null,
      })
    : null;

  const region = [CountryCode.FR, CountryCode.BE].includes(options.country)
    ? translatedRegion
    : options.region;

  const arr = [options.city, region, country].filter((x) => !!x);
  if (arr && arr.length) return arr.map((m) => m).join(', ');
};

export const formatApiAddress = (options: {
  streetNumber?: string | null;
  street?: string | null;
  zip?: string | null;
  city?: string | null;
}): string | undefined => {
  let formattedStreet = options.streetNumber;
  if (options.street) {
    formattedStreet = options.streetNumber
      ? formattedStreet?.concat(' ').concat(options.street)
      : options.street;
  }
  const arr = [formattedStreet, options.zip, options.city].filter((x) => !!x);
  if (arr && arr.length) return arr.map((m) => m).join(', ');
};

export const formatShelterDetailsAddress = (
  options: {
    streetNumber?: string | null;
    street?: string | null;
    zip?: string | null;
    city?: string | null;
    region?: string | null;
    country?: string | null;
  },
  translate: Translate,
): string | undefined => {
  let formattedStreet = options.streetNumber;
  if (options.street) {
    formattedStreet = options.streetNumber
      ? formattedStreet?.concat(' ').concat(options.street)
      : options.street;
  }

  const country = options.country ? translate(`enum.country.${options.country}`) : null;
  const translatedRegion = options.region
    ? translate(`enum.regionCode.${options.region}`, {
        defaultValue: null,
      })
    : null;

  const region = [CountryCode.FR, CountryCode.BE].includes(options.country)
    ? translatedRegion
    : options.region;

  const arr = [formattedStreet, options.zip, options.city, region, country].filter((x) => !!x);
  if (arr && arr.length) return arr.map((m) => m).join(', ');
};

export const formatApiAddressCity = (options: {
  city?: string | null;
  zip?: string | null;
}): string | undefined => {
  const arr = [options.city, options.zip].filter((x) => !!x);
  if (arr && arr.length) return arr.map((m) => capitalize(m as string)).join(' - ');
};

export const formatLocation = (
  options: {
    country?: CountryCode | null;
    region?: string | null;
  },
  translate: Translate,
): string => {
  const translatedCountry = options.country ? translate(`enum.countryCode.${options.country}`) : '';

  const translatedRegion = options.region
    ? translate(`enum.regionCode.${options.region}`, {
        defaultValue: '',
      })
    : '';

  return `${translatedCountry}${translatedRegion ? ` - ${translatedRegion}` : ''}`;
};

export const formatLanguages = (
  currentLanguage: Language,
  languages: string[],
): string | undefined => {
  if (!languages || !languages.length) return;
  const languageNames = new (Intl as any).DisplayNames([currentLanguage], {
    type: 'language',
  });
  return languages.map((lng) => capitalize(languageNames.of(lng.trim()))).join(', ');
};

export const formatPedigreeType = (translate: Translate, value?: string | null): string => {
  if (!value || value === 'no_pedigree') return translate('glossary.noPedigree');
  if (value === 'id1' || value === 'FCI') return translate('glossary.pedigreeFci');
  const pedigreeMap: Record<string, string> = translate('enum.pedigreeTypes', {
    returnObjects: true,
  });
  if (pedigreeMap[value] != null && typeof pedigreeMap[value] === 'string') {
    return `${translate('glossary.otherPedigree')} (${pedigreeMap[value]})`;
  }
  return `${translate('glossary.otherPedigree')} (${value})`;
};

export const formatBreederRating = (
  reviews: { globalGrade: number | null }[],
):
  | {
      amount: number;
      average: number;
    }
  | undefined => {
  if (!reviews?.length) return;
  return {
    amount: reviews.length,
    average:
      reviews.reduce((total, review) => total + (review?.globalGrade ?? 0), 0) / reviews.length,
  };
};

export const joinWithAnd = (translate: Translate, arr: any[]): string => {
  if (arr.length === 1) return arr[0];
  const firsts = arr.slice(0, arr.length - 1);
  const last = arr[arr.length - 1];
  return `${firsts.join(', ')} ${translate('glossary.and')} ${last}`;
};

export const formatProDegree = (
  value?: NonNullable<ProGetProQuery['searchProfessionals']['professionals']>[number]['degrees'],
): string | undefined => {
  if (!value?.length) return;
  return value
    .map((d) => `${d.degree}, ${d.dateEnd ? dayjs(d.dateEnd).format('YYYY') : ''} (${d.school})`)
    .join('\n');
};

export const formatPhoneNumber = (phoneNumber: string, phonePrefixCountry: string) => {
  let phoneNumberWithoutLeadingZero = phoneNumber;

  if (
    SUPPORTED_COUNTRIES.includes(phonePrefixCountry as CountryCode) &&
    phoneNumber.length === 10 &&
    phoneNumber.startsWith('0')
  ) {
    phoneNumberWithoutLeadingZero = phoneNumber.slice(1);
  }

  return `${COUNTRY_CODES_WITH_PHONE_VALUES[phonePrefixCountry]}${phoneNumberWithoutLeadingZero}`;
};

export const splitPhoneNumberIntoParts = (phoneNumber: string) => {
  const trimmedPhoneNumber = phoneNumber.trim();

  const phonePrefixCountry = Object.keys(COUNTRY_CODES_WITH_PHONE_VALUES).find((key) =>
    trimmedPhoneNumber.startsWith(COUNTRY_CODES_WITH_PHONE_VALUES[key]),
  ) as 'FR' | 'BE' | 'NL' | 'DE' | 'CH' | 'EU' | undefined;

  const phoneNumberWithoutPrefix = phonePrefixCountry
    ? trimmedPhoneNumber.replace(COUNTRY_CODES_WITH_PHONE_VALUES[phonePrefixCountry], '')
    : trimmedPhoneNumber;

  return {
    phoneNumberWithoutPrefix,
    phonePrefixCountry,
  };
};

export const formatAccountContactPreferences = (translate: Translate) => {
  const emails: ContactPreferenceItem[] = [
    {
      title: translate('mt.preferences.contact.titles.educational'),
      items: [
        {
          content: translate('mt.preferences.contact.items.newsletter'),
          items: [ContactPreference.MAILJET],
        },
        {
          content: translate('mt.preferences.contact.items.products'),
          items: [ContactPreference.KLAVIYO],
        },
      ],
    },
    {
      title: translate('mt.preferences.contact.titles.communication'),
      items: [
        {
          content: translate('mt.preferences.contact.items.applications'),
          items: [
            ContactPreference.EMAIL_ON_APPLICATION_UPDATE,
            ContactPreference.SMS_ON_APPLICATION_UPDATE,
          ],
        },
        {
          content: translate('mt.preferences.contact.items.litters'),
          items: [ContactPreference.EMAIL_ON_LITTER_UPDATE],
        },
        {
          content: translate('mt.preferences.contact.items.messages'),
          items: [ContactPreference.EMAIL_ON_MESSAGE_RECEIVED],
        },
      ],
    },
    {
      title: translate('mt.preferences.contact.titles.notifications'),
      items: [
        {
          content: translate('mt.preferences.contact.items.breedInterest'),
          items: [
            ContactPreference.RECURRENT_UDPATES_NEW_BREEDERS_LITTERS,
            ContactPreference.BREED_INTEREST,
          ],
        },
        {
          content: translate('mt.preferences.contact.items.onboarding'),
          items: [ContactPreference.BREEDER_ONBOARDING_UNFINISHED],
        },
        {
          content: translate('mt.preferences.contact.items.bookings'),
          items: [ContactPreference.EMAIL_ON_BOOKING_UPDATE],
        },
      ],
    },
  ];

  return emails;
};
export const formatPublicContactPreferences = (translate: Translate) => {
  const emails: ContactPreferenceItem[] = [
    {
      title: translate('mt.preferences.contact.titles.educational'),
      items: [
        {
          content: translate('mt.preferences.contact.items.newsletter'),
          items: [ContactPreference.MAILJET],
        },
        {
          content: translate('mt.preferences.contact.items.products'),
          items: [ContactPreference.KLAVIYO],
        },
      ],
    },
    {
      title: translate('mt.preferences.contact.titles.notifications'),
      items: [
        {
          content: translate('mt.preferences.contact.items.breedInterest'),
          items: [
            ContactPreference.RECURRENT_UDPATES_NEW_BREEDERS_LITTERS,
            ContactPreference.BREED_INTEREST,
          ],
        },
      ],
    },
  ];
  return emails;
};

export const formatContactPreferences = (translate: Translate, isRegisteredUser?: boolean) => {
  const accountContactPreferences = formatAccountContactPreferences(translate);
  const publicContactPreferences = formatPublicContactPreferences(translate);

  if (isRegisteredUser) {
    return accountContactPreferences;
  }
  return publicContactPreferences;
};

export const formatPuppiesSold = (value: number) => {
  if (value <= 5) return value.toString();

  if (value < 10) return '5+';
  return '10+';
};
